import * as React from "react";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export interface EvidenceCaptureItemProps {
  timestamp: string;
  captureType: "auto" | "manual";
  status?: "captured" | "pending" | "failed" | string;
  caseName?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * EvidenceCaptureItem - Reusable component for displaying evidence capture entries
 *
 * Usage:
 * <EvidenceCaptureItem
 *   timestamp="11/21/2025 11:32:15 AM"
 *   captureType="auto"
 *   status="captured"
 *   caseName="Case #001"
 *   onClick={() => handleViewEvidence(id)}
 * />
 */
export function EvidenceCaptureItem({
  timestamp,
  captureType,
  status = "captured",
  caseName,
  onClick,
  className,
}: EvidenceCaptureItemProps) {
  const formattedDate = React.useMemo(() => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return timestamp;
    }
  }, [timestamp]);

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors",
        onClick && "cursor-pointer",
        className,
      )}
      onClick={onClick}
    >
      <div className="flex-shrink-0 mt-0.5">
        <FileText className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-foreground">
            {formattedDate}
          </div>
          {caseName && (
            <>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs font-medium text-blue-600 truncate">
                {caseName}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground capitalize">
            {captureType}
          </span>
          {status && (
            <>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground capitalize">
                {status}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
